import { Link } from "react-router-dom";
import { ShieldCheck, Lock, KeyRound, Users, ScrollText, ServerCog, FileMinus2, DatabaseZap } from "lucide-react";
import { Seo } from "@/components/Seo";

const CONTROLS = [
  { icon: Lock, h: "Encryption in transit", p: "All traffic runs over TLS/HTTPS with Secure, HttpOnly, SameSite cookies and HSTS enforced." },
  { icon: KeyRound, h: "MFA & strong auth", p: "Bcrypt-hashed passwords, optional TOTP two-factor authentication with backup codes, and brute-force lockout." },
  { icon: Users, h: "Role-based access", p: "Granular permissions per role — owner, manager, analyst, viewer — enforced on every endpoint." },
  { icon: DatabaseZap, h: "Tenant isolation", p: "Every record is scoped to your firm. No firm can ever read or write another firm's data." },
  { icon: ScrollText, h: "Audit logging", p: "Security-relevant actions — logins, MFA changes, admin actions, data changes — are logged with IP and timestamp." },
  { icon: ServerCog, h: "Least-privilege admin", p: "Platform administration is restricted and gated; firm users can never reach super-admin functions." },
  { icon: FileMinus2, h: "Data minimization", p: "We don't store SSNs, medical records, full card numbers or case documents. Free-text is scrubbed of sensitive patterns." },
  { icon: ShieldCheck, h: "Export & deletion", p: "Export your complete data set anytime, and request full deletion of your firm's data on demand." },
];

export default function SecurityTrust() {
  return (
    <div className="min-h-screen bg-[#0A1B2E] text-white font-body" data-testid="security-page">
      <Seo title="Security & Trust — RetainProof" description="How RetainProof protects law-firm marketing, vendor and retained-matter data: TLS encryption, MFA, role-based access, tenant isolation, audit logging and data minimization." path="/security" />
      <header className="h-16 flex items-center justify-between px-6 sm:px-10 border-b border-white/10">
        <Link to="/" className="flex items-center gap-2.5 font-body font-bold text-2xl tracking-tight">
          <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-8 w-auto" />
          <span>Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></span>
        </Link>
        <Link to="/" className="font-mono-data text-xs uppercase tracking-widest text-zinc-400 hover:text-white">← Home</Link>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-16">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#D5A44A] mb-4">Trust &amp; Security</p>
        <h1 className="font-display text-4xl sm:text-5xl font-light leading-tight">
          Built for law-firm confidential data.
        </h1>
        <p className="mt-6 text-lg text-zinc-400 max-w-2xl leading-relaxed">
          RetainProof handles your marketing, vendor and retained-matter data with controls designed
          for professional-services confidentiality — and documented to support a future SOC 2 review.
        </p>

        <div className="mt-14 grid sm:grid-cols-2 gap-5" data-testid="security-controls">
          {CONTROLS.map((c) => (
            <div key={c.h} className="border border-white/10 bg-[#12324D] p-6" data-testid={`security-control-${c.h.toLowerCase().replace(/\W+/g, "-")}`}>
              <c.icon className="h-6 w-6 text-[#D5A44A]" />
              <h2 className="font-display text-xl mt-4">{c.h}</h2>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{c.p}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 border-l-2 border-[#0E7C7B] pl-6 py-2">
          <h2 className="font-display text-2xl">What we intentionally don't store</h2>
          <p className="mt-3 text-zinc-400 leading-relaxed max-w-2xl">
            RetainProof minimizes sensitive data by design. We do not store Social Security numbers,
            medical records or PHI, full payment-card numbers (payments run through Stripe), or
            substantive case documents. Sensitive patterns entered in free-text fields are automatically redacted.
          </p>
        </div>

        <div className="mt-12 border border-white/10 bg-[#12324D] p-6">
          <h2 className="font-display text-2xl">SOC 2 readiness</h2>
          <p className="mt-3 text-zinc-400 leading-relaxed max-w-2xl">
            Our controls are mapped to the SOC 2 Trust Services Criteria across Security, Availability,
            Confidentiality and Privacy. Encryption at rest, automated backups and continuous monitoring
            are provided through our managed production infrastructure.
          </p>
        </div>

        <p className="mt-16 text-xs text-zinc-600 font-mono-data leading-relaxed">
          RetainProof™ · Provided by CMD Marketing Enterprises LLC through CMD+DesignLab.
          <br />This overview describes current controls and is provided for informational purposes.
        </p>
      </div>
    </div>
  );
}
